import NetInfo, { NetInfoState, NetInfoSubscription } from '@react-native-community/netinfo';
import {
  applyDetoxNetworkOverride,
  subscribeToDetoxNetworkOverride,
} from '../utils/detoxTestControls';

export type OfflineNetworkSource = 'network' | 'detox';

type NetworkStateLike = Pick<NetInfoState, 'isConnected' | 'isInternetReachable'>;
type NetworkStateListener = (state: NetworkStateLike) => void;
type BooleanNetworkListener = (isOnline: boolean) => void;
type Unsubscribe = () => void;

interface OfflineNetworkMonitorDependencies {
  fetchNetworkState: () => Promise<NetworkStateLike>;
  subscribeToNetworkState: (listener: NetworkStateListener) => Unsubscribe;
  applyNetworkOverride: (state: NetworkStateLike) => boolean;
  subscribeToNetworkOverride: (listener: BooleanNetworkListener) => Unsubscribe;
  onStatusChange: BooleanNetworkListener;
  onBecameOnline: (source: OfflineNetworkSource) => void | Promise<void>;
  onBecameOnlineError?: (source: OfflineNetworkSource, error: unknown) => void;
}

export class OfflineNetworkMonitor {
  private isOnline = false;
  private networkUnsubscribe: Unsubscribe | null = null;
  private overrideUnsubscribe: Unsubscribe | null = null;
  private isStarted = false;

  constructor(private readonly dependencies: OfflineNetworkMonitorDependencies) {}

  start(): void {
    if (this.isStarted) {
      return;
    }

    this.isStarted = true;

    this.dependencies.fetchNetworkState()
      .then(state => {
        this.setOnline(this.dependencies.applyNetworkOverride(state));
      })
      .catch(() => {
        this.setOnline(false);
      });

    this.networkUnsubscribe = this.dependencies.subscribeToNetworkState(state => {
      this.handleStatusChange(this.dependencies.applyNetworkOverride(state), 'network');
    });

    this.overrideUnsubscribe = this.dependencies.subscribeToNetworkOverride(isOnline => {
      this.handleStatusChange(isOnline, 'detox');
    });
  }

  getIsOnline(): boolean {
    return this.isOnline;
  }

  cleanup(): void {
    this.networkUnsubscribe?.();
    this.overrideUnsubscribe?.();
    this.networkUnsubscribe = null;
    this.overrideUnsubscribe = null;
    this.isStarted = false;
    this.setOnline(false);
  }

  private handleStatusChange(isOnline: boolean, source: OfflineNetworkSource): void {
    const wasOffline = !this.isOnline;
    this.setOnline(isOnline);

    if (wasOffline && this.isOnline) {
      Promise.resolve(this.dependencies.onBecameOnline(source)).catch(error => {
        this.dependencies.onBecameOnlineError?.(source, error);
      });
    }
  }

  private setOnline(isOnline: boolean): void {
    this.isOnline = isOnline;
    this.dependencies.onStatusChange(isOnline);
  }
}

interface DefaultOfflineNetworkMonitorCallbacks {
  onStatusChange: BooleanNetworkListener;
  onBecameOnline: (source: OfflineNetworkSource) => void | Promise<void>;
  onBecameOnlineError?: (source: OfflineNetworkSource, error: unknown) => void;
}

export function createOfflineNetworkMonitor(
  callbacks: DefaultOfflineNetworkMonitorCallbacks
): OfflineNetworkMonitor {
  return new OfflineNetworkMonitor({
    fetchNetworkState: () => NetInfo.fetch(),
    subscribeToNetworkState: listener =>
      NetInfo.addEventListener(listener as (state: NetInfoState) => void) as NetInfoSubscription,
    applyNetworkOverride: applyDetoxNetworkOverride,
    subscribeToNetworkOverride: subscribeToDetoxNetworkOverride,
    ...callbacks,
  });
}
